import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container/Container";
import { Box } from "@whoosh/design-system/src/components/primitive/Box/Box";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { EventsList } from "./EventsList";
import { FeaturedEventsFragment_club$key } from "./__generated__/FeaturedEventsFragment_club.graphql";

export const FeaturedEvents = ({
  clubRef,
}: {
  clubRef: FeaturedEventsFragment_club$key | null;
}) => {
  const club = useFragment(FeaturedEventsFragmentString, clubRef);
  const { menuAndNavUpdate } = useFlags();

  const featuredEventsConnection = club?.featuredEvents ?? null;
  const nonFeaturedEventsConnection = club?.nonFeaturedEvents ?? null;

  const hasFeaturedEvents = (club?.featuredEvents?.count ?? 0) > 0;
  const hasNonFeaturedEvents = (club?.nonFeaturedEvents?.count ?? 0) > 0;
  const hasEventsEnabled = !!club?.departments?.edges?.some(
    (edge) => edge?.node?.anyFacilityEventsEnabled
  );
  const eventNounPlural = getNounOverride(
    club?.copyOverrides?.event?.plural,
    t`Events`
  );

  if (!hasEventsEnabled || (!hasFeaturedEvents && !hasNonFeaturedEvents)) {
    return null;
  }

  const eventsConnection = hasFeaturedEvents
    ? featuredEventsConnection
    : nonFeaturedEventsConnection;

  if (!eventsConnection) {
    return null;
  }

  return menuAndNavUpdate ? (
    <Box paddingHorizontal={{ base: 0, lg: 12 }} pb={4}>
      <EventsList
        eventListFragRef={eventsConnection}
        loadedFeaturedEvents={hasFeaturedEvents}
        eventNounPlural={eventNounPlural}
      />
    </Box>
  ) : (
    <Container pb={{ base: 4, md: 4 }} paddingHorizontal={{ base: 0, lg: 16 }}>
      <EventsList
        eventListFragRef={eventsConnection}
        loadedFeaturedEvents={hasFeaturedEvents}
        eventNounPlural={eventNounPlural}
      />
    </Container>
  );
};

export const FeaturedEventsFragmentString = graphql`
  fragment FeaturedEventsFragment_club on Club
  @argumentDefinitions(dateGTE: { type: "Date!" }) {
    departments {
      edges {
        node {
          anyFacilityEventsEnabled
        }
      }
    }
    copyOverrides {
      event {
        plural
      }
    }
    featuredEvents: events(first: 5, dateGte: $dateGTE, isFeatured: true) {
      count
      ...EventsList_EventsConnection
    }
    nonFeaturedEvents: events(first: 5, dateGte: $dateGTE, isFeatured: false) {
      count
      ...EventsList_EventsConnection
    }
  }
`;
